/**
 * Les heures d'une maintenance, dites au candidat — RG-9.10, [DECISIONS §196].
 *
 * **Le fuseau du CANDIDAT, jamais celui du serveur.** La pile tourne en UTC ;
 * afficher son heure ferait lire « fermeture à 6 h 00 » à quelqu'un qui la
 * subira à 2 h 00. Quatre ou cinq heures d'écart selon la saison — assez pour
 * qu'un bandeau exact soit lu comme un bandeau faux.
 *
 * **Le chiffre est TOUJOURS là.** « Une maintenance est prévue » ne dit rien de
 * l'urgence ; « dans 12 minutes » se comprend sans y penser. C'est le même
 * raisonnement que `VerificationBanner` (RG-1.3), et il vaut ici pour la même
 * raison : le candidat doit pouvoir décider, en une seconde, s'il a le temps de
 * commencer son examen.
 *
 * Ces fonctions sont PURES : elles se testent sans horloge et sans réseau.
 */

/** Le fuseau par défaut — celui de `users.timezone`, et celui du produit. */
export const FUSEAU_PAR_DEFAUT = 'America/Port-au-Prince'

/**
 * `2h30` — la forme déjà employée par les courriels d'abonnement.
 *
 * Une seule forme dans tout le produit : deux notations d'heure font douter que
 * les deux parlent du même événement.
 */
export function heureDuCandidat(iso: string, fuseau = FUSEAU_PAR_DEFAUT): string {
  const rendu = new Intl.DateTimeFormat('fr-FR', {
    timeZone: fuseau,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso))

  const [heures = '0', minutes = '00'] = rendu.split(':')

  // Sans le zéro de tête : « 2h30 » se lit, « 02h30 » se déchiffre.
  return `${String(Number(heures))}h${minutes}`
}

/** `samedi 16 août` — le jour, quand la coupure n'est pas pour aujourd'hui. */
export function jourDuCandidat(iso: string, fuseau = FUSEAU_PAR_DEFAUT): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: fuseau,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso))
}

/** Les deux instants tombent-ils le même jour, dans le fuseau du candidat ? */
export function memeJour(a: string, b: string, fuseau = FUSEAU_PAR_DEFAUT): boolean {
  const clef = (iso: string): string =>
    new Intl.DateTimeFormat('fr-CA', {
      timeZone: fuseau,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso))

  return clef(a) === clef(b)
}

/**
 * Le délai, en mots, avec le chiffre dedans.
 *
 * **Jamais de compteur à la seconde.** Une horloge qui s'égrène sous les yeux
 * pendant qu'on révise est une pression permanente, et 06 §6.3 interdit déjà le
 * clignotement pour les mêmes raisons. La minute suffit à décider.
 */
export function delaiEnMots(millisecondes: number): string {
  if (millisecondes <= 0) return 'maintenant'

  const minutes = Math.floor(millisecondes / 60_000)

  if (minutes < 1) return 'moins d’une minute'
  if (minutes < 60) return `${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'}`

  const heures = Math.floor(minutes / 60)
  const reste = minutes % 60

  // Au-delà de deux jours, l'heure exacte n'apprend plus rien : « dans
  // 63 heures » se recompte de tête, « dans 3 jours » se comprend.
  if (heures >= 48) return `${String(Math.floor(heures / 24))} jours`

  if (reste === 0) return `${String(heures)} ${heures === 1 ? 'heure' : 'heures'}`

  return `${String(heures)} h ${String(reste).padStart(2, '0')}`
}

/** Les trois tons du bandeau — RG-9.10. */
export type Ton = 'sobre' | 'precis' | 'urgent'

/**
 * **Le ton se durcit, il ne crie pas d'emblée.**
 *
 * `alert` six heures à l'avance apprend à ignorer les alertes ; c'est le
 * reproche exact que `VerificationBanner` s'adresse à lui-même. Les seuils :
 * plus de six heures, on informe ; moins, on avertit ; moins de trente minutes,
 * on alerte, parce qu'à ce moment-là une épreuve commencée ne sera pas finie.
 */
export function tonDeLAnnonce(millisecondes: number): Ton {
  if (millisecondes > 6 * 3_600_000) return 'sobre'
  if (millisecondes > 30 * 60_000) return 'precis'

  return 'urgent'
}

/**
 * La plus longue épreuve du produit, en minutes — RG-3.17.
 *
 * L'examen final tire quarante questions à deux minutes chacune : quatre-vingts
 * minutes, plus la marge de lecture du récapitulatif. C'est cette durée qui
 * décide si l'on a encore le temps de commencer (RG-9.8) — un seuil plus court
 * autoriserait à démarrer une épreuve qu'on ne pourra pas remettre, ce qui est
 * précisément le seul cas où le candidat perd son travail.
 */
export const MINUTES_D_UNE_EPREUVE = 90
